/**
 * Реестр локалей (04 §6.1). Отсюда берутся: переключатель языка, направление
 * письма, шрифтовой стек по скрипту (05 §3) и список для i18n:check.
 */

export type Direction = 'ltr' | 'rtl';

/**
 * Скрипт определяет шрифтовой стек. Значение попадает в data-script на <html>,
 * а CSS по нему переключает --font-display/--font-body/--font-mono.
 * Хардкодить font-family в компонентах запрещено (05 §3).
 */
export type ScriptName =
  | 'latin'
  | 'greek'
  | 'ja'
  | 'zh-hans'
  | 'zh-hant'
  | 'ko'
  | 'thai'
  | 'devanagari'
  | 'arabic'
  | 'hebrew';

export interface LocaleMeta {
  code: string;
  /** Английское название, для служебных списков. */
  name: string;
  /** Название на своём языке - именно оно в переключателе. */
  nativeName: string;
  dir: Direction;
  script: ScriptName;
  /** Прошла ручную вычитку человеком (04 §6.1). Машинный перевод - false. */
  reviewed: boolean;
}

export const DEFAULT_LOCALE = 'en';

/**
 * Кириллица и латиница делят один шрифтовой стек (Unbounded + Inter + IBM Plex Mono
 * покрывают оба), поэтому отдельного скрипта 'cyrillic' здесь нет.
 */
export const LOCALES: readonly LocaleMeta[] = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    dir: 'ltr',
    script: 'latin',
    reviewed: true,
  },
  {
    code: 'ru',
    name: 'Russian',
    nativeName: 'Русский',
    dir: 'ltr',
    script: 'latin',
    reviewed: true,
  },
  {
    code: 'uk',
    name: 'Ukrainian',
    nativeName: 'Українська',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'pt-BR',
    name: 'Portuguese (Brazil)',
    nativeName: 'Português (Brasil)',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'it',
    name: 'Italian',
    nativeName: 'Italiano',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'pl',
    name: 'Polish',
    nativeName: 'Polski',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'nl',
    name: 'Dutch',
    nativeName: 'Nederlands',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'tr',
    name: 'Turkish',
    nativeName: 'Türkçe',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'cs',
    name: 'Czech',
    nativeName: 'Čeština',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'ro',
    name: 'Romanian',
    nativeName: 'Română',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'hu',
    name: 'Hungarian',
    nativeName: 'Magyar',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'el',
    name: 'Greek',
    nativeName: 'Ελληνικά',
    dir: 'ltr',
    script: 'greek',
    reviewed: false,
  },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', dir: 'ltr', script: 'ja', reviewed: false },
  { code: 'ko', name: 'Korean', nativeName: '한국어', dir: 'ltr', script: 'ko', reviewed: false },
  {
    code: 'zh-Hans',
    name: 'Chinese (Simplified)',
    nativeName: '简体中文',
    dir: 'ltr',
    script: 'zh-hans',
    reviewed: false,
  },
  {
    code: 'zh-Hant',
    name: 'Chinese (Traditional)',
    nativeName: '繁體中文',
    dir: 'ltr',
    script: 'zh-hant',
    reviewed: false,
  },
  {
    code: 'id',
    name: 'Indonesian',
    nativeName: 'Bahasa Indonesia',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  {
    code: 'vi',
    name: 'Vietnamese',
    nativeName: 'Tiếng Việt',
    dir: 'ltr',
    script: 'latin',
    reviewed: false,
  },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', dir: 'ltr', script: 'thai', reviewed: false },
  {
    code: 'hi',
    name: 'Hindi',
    nativeName: 'हिन्दी',
    dir: 'ltr',
    script: 'devanagari',
    reviewed: false,
  },
  {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    dir: 'rtl',
    script: 'arabic',
    reviewed: false,
  },
  {
    code: 'he',
    name: 'Hebrew',
    nativeName: 'עברית',
    dir: 'rtl',
    script: 'hebrew',
    reviewed: false,
  },
];

export const LOCALE_CODES: readonly string[] = LOCALES.map((l) => l.code);

const BY_CODE = new Map(LOCALES.map((l) => [l.code.toLowerCase(), l]));

export function findLocale(code: string): LocaleMeta | undefined {
  return BY_CODE.get(code.toLowerCase());
}

/**
 * Подбор локали по тегу браузера: сначала точное совпадение, затем базовый язык.
 * 'zh-CN' -> zh-Hans, 'zh-TW' -> zh-Hant, 'pt' -> pt-BR (единственный португальский).
 */
export function resolveLocale(tag: string): LocaleMeta | undefined {
  const lower = tag.toLowerCase();
  const exact = BY_CODE.get(lower);
  if (exact) return exact;

  if (lower.startsWith('zh')) {
    const hant = /hant|tw|hk|mo/.test(lower);
    return BY_CODE.get(hant ? 'zh-hant' : 'zh-hans');
  }

  const base = lower.split('-')[0];
  if (!base) return undefined;
  return BY_CODE.get(base) ?? LOCALES.find((l) => l.code.toLowerCase().startsWith(`${base}-`));
}

/** Первая подходящая локаль из списка предпочтений браузера, иначе английский. */
export function negotiateLocale(tags: readonly string[]): LocaleMeta {
  for (const tag of tags) {
    const found = resolveLocale(tag);
    if (found) return found;
  }
  const fallback = findLocale(DEFAULT_LOCALE);
  if (!fallback) throw new Error('default locale missing from registry');
  return fallback;
}
